import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, CheckCircle, AlertCircle } from 'lucide-react';

const JobApplicationModal = ({ isOpen, onClose, captchaToken }) => {
    const [formData, setFormData] = useState({
        characterName: '',
        phone: '',
        discordTag: '',
        hasExperience: 'No',
        reason: '',
    });
    const [status, setStatus] = useState('idle'); // idle, submitting, success, error
    const [message, setMessage] = useState('');

    // Disable body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('submitting');
        setMessage('');

        try {
            const response = await fetch('/api/send-discord-message', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    type: 'Job Application',
                    data: formData,
                    captchaToken: captchaToken,
                }),
            });

            if (response.ok) {
                setStatus('success');
                setMessage('Application sent successfully!');
                setTimeout(() => {
                    onClose();
                    setStatus('idle');
                    setFormData({ characterName: '', phone: '', discordTag: '', hasExperience: 'No', reason: '' });
                    setMessage('');
                }, 2000);
            } else {
                if (response.status === 403) {
                    const errorData = await response.json();
                    setStatus('error');
                    setMessage(errorData.message);
                    return;
                }
                setStatus('error');
                setMessage('Failed to send application. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setStatus('error');
            setMessage('An error occurred. Please try again later.');
        }
    };

    return ReactDOM.createPortal(
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
                zIndex: 9999, // Ensure it's above everything
                background: 'rgba(0, 0, 0, 0.8)',
                backdropFilter: 'blur(8px)',
            }}
            onClick={(e) => {
                // Close on backdrop click
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className="p-4 rounded-4 position-relative glass-panel"
                style={{
                    width: '90%',
                    maxWidth: '400px',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--glass-border)',
                    boxShadow: 'var(--glass-shadow)',
                }}
            >
                <button
                    onClick={onClose}
                    className="position-absolute top-0 end-0 m-3 btn btn-link text-white p-0"
                    style={{ textDecoration: 'none', color: 'var(--text-muted)' }}
                >
                    <X size={24} />
                </button>

                <h3
                    className="fw-bold mb-4 text-center"
                    style={{
                        fontFamily: "var(--font-heading)",
                        color: 'var(--text-main)',
                        letterSpacing: '1px'
                    }}
                >
                    Job Application
                </h3>

                {status === 'success' ? (
                    <div className="text-center py-5">
                        <CheckCircle size={64} className="text-success mb-3" />
                        <h4 className="text-white">Application Sent!</h4>
                        <p className="text-white-50">{message}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label" style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Character Name</label>
                            <input
                                type="text"
                                name="characterName"
                                value={formData.characterName}
                                onChange={handleChange}
                                required
                                className="form-control"
                                style={{
                                    background: 'var(--bg-surface)',
                                    border: '1px solid var(--glass-border)',
                                    color: 'var(--text-main)',
                                    borderRadius: '4px',
                                    padding: '10px 15px'
                                }}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label" style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Discord Tag</label>
                            <input
                                type="text"
                                name="discordTag"
                                value={formData.discordTag}
                                onChange={handleChange}
                                required
                                placeholder="e.g. username#0000"
                                className="form-control"
                                style={{
                                    background: 'var(--bg-surface)',
                                    border: '1px solid var(--glass-border)',
                                    color: 'var(--text-main)',
                                    borderRadius: '4px',
                                    padding: '10px 15px'
                                }}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label" style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Phone Number</label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                className="form-control"
                                style={{
                                    background: 'var(--bg-surface)',
                                    border: '1px solid var(--glass-border)',
                                    color: 'var(--text-main)',
                                    borderRadius: '4px',
                                    padding: '10px 15px'
                                }}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label" style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Experience in Dealership?</label>
                            <select
                                name="hasExperience"
                                value={formData.hasExperience}
                                onChange={handleChange}
                                className="form-select"
                                style={{
                                    background: 'var(--bg-surface)',
                                    border: '1px solid var(--glass-border)',
                                    color: 'var(--text-main)',
                                    borderRadius: '4px',
                                    padding: '10px 15px'
                                }}
                            >
                                <option value="No" style={{ color: '#000' }}>No</option>
                                <option value="Yes" style={{ color: '#000' }}>Yes</option>
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="form-label" style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Reason for Joining</label>
                            <textarea
                                name="reason"
                                value={formData.reason}
                                onChange={handleChange}
                                rows="3"
                                className="form-control"
                                style={{
                                    background: 'var(--bg-surface)',
                                    border: '1px solid var(--glass-border)',
                                    color: 'var(--text-main)',
                                    borderRadius: '4px',
                                    padding: '10px 15px'
                                }}
                            ></textarea>
                        </div>

                        {status === 'error' && (
                            <div className="alert alert-danger d-flex align-items-center gap-2 mb-3">
                                <AlertCircle size={18} />
                                {message}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={status === 'submitting'}
                            className="btn btn-luxury w-100 py-3 fw-bold"
                            style={{
                                borderRadius: '4px',
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                fontSize: '0.9rem'
                            }}
                        >
                            {status === 'submitting' ? 'Sending...' : 'Submit Application'}
                        </button>
                    </form>
                )}
            </div>
        </div>,
        document.body
    );
};

export default JobApplicationModal;
